import { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import api from "@/lib/api";
import { StaffHeader } from "@/components/StaffHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, FileDown, Ban, Trash2 } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const FILTERS = [["tous", "Tous"], ["actifs", "Actifs"], ["expires", "Expirés"], ["retires", "Retirés"]];
const dtf = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Paris" }) : "—";
const STCOL = { actif: "bg-emerald-100 text-emerald-700", retiré: "bg-rose-100 text-rose-700", expiré: "bg-amber-100 text-amber-800", refusé: "bg-muted text-muted-foreground" };

export default function ConsentsAdmin() {
  const confirm = useConfirm();
  const [filter, setFilter] = useState("tous");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get(`/admin/consents?filter=${filter}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const withdraw = async (id) => {
    if (!(await confirm({ title: "Retrait du consentement", description: "Enregistrer le retrait de ce consentement ?", confirmText: "Enregistrer le retrait", destructive: true }))) return;
    api.post(`/admin/consents/${id}/withdraw`, { moyen: "retrait enregistré par l'officine" }).then(() => { toast.success("Retrait enregistré."); load(); }).catch(() => toast.error("Erreur."));
  };

  const del = async (id) => {
    if (!(await confirm({ title: "Supprimer la preuve", description: "Supprimer définitivement cette preuve de consentement ? Action irréversible.", confirmText: "Supprimer", destructive: true }))) return;
    api.delete(`/admin/consents/${id}`).then(() => { toast.success("Consentement supprimé."); load(); }).catch(() => toast.error("Erreur."));
  };

  const purgeOld = async () => {
    if (!(await confirm({ title: "Supprimer les anciens", description: "Supprimer définitivement tous les consentements de plus de 12 mois pour faire de la place ? Action irréversible.", confirmText: "Supprimer les anciens", destructive: true }))) return;
    api.post("/admin/consents/purge-old", { months: 12 }).then((r) => { toast.success(`${r.data.deleted} consentement(s) ancien(s) supprimé(s).`); load(); }).catch(() => toast.error("Erreur."));
  };

  const exportPdf = (c) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" }); const W = doc.internal.pageSize.getWidth(); let y = 56;
    doc.setFillColor(6, 78, 59); doc.rect(0, 0, W, 8, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(6, 78, 59);
    doc.text("Preuve de consentement (art. 7.1 RGPD)", 40, y); y += 24;
    doc.setFontSize(10); doc.setTextColor(20, 20, 20);
    const rows = [
      ["Identifiant", c.id], ["E-mail", c.email || "—"], ["Personne", c.identifiant || "—"], ["Type", c.type], ["Valeur", c.valeur],
      ["Statut actuel", c.statut], ["Texte exact accepté", c.texte_affiche], ["Version du libellé", c.version_libelle],
      ["URL du formulaire", c.url_formulaire], ["Date et heure (UTC)", c.date_heure], ["Adresse IP", c.adresse_ip || "—"],
      ["Statut opt-in", c.statut_double_optin], ["Date de retrait", c.date_retrait || "—"], ["Moyen de retrait", c.moyen_retrait || "—"],
    ];
    doc.setFont("helvetica", "normal");
    rows.forEach(([k, v]) => {
      doc.setFont("helvetica", "bold"); doc.text(`${k} :`, 40, y);
      doc.setFont("helvetica", "normal");
      doc.splitTextToSize(String(v ?? "—"), W - 210).forEach((ln, i) => { doc.text(ln, 200, y + i * 13); });
      y += Math.max(16, doc.splitTextToSize(String(v ?? "—"), W - 210).length * 13);
    });
    y += 10; doc.setDrawColor(200); doc.line(40, y, W - 40, y); y += 16;
    doc.setFontSize(9); doc.setTextColor(90, 90, 90);
    doc.text(doc.splitTextToSize("Document généré par Pharmacie des Écoles à titre de preuve du consentement. Journal en ajout seul.", W - 80), 40, y);
    doc.save(`preuve-consentement-${c.id}.pdf`);
  };

  return (
    <div className="min-h-screen bg-background">
      <StaffHeader />
      <main className="mx-auto max-w-4xl px-4 py-8">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground"><ShieldCheck className="h-5 w-5" /></div>
            <div><h1 className="font-heading text-2xl font-bold leading-tight">Consentements</h1><p className="text-[11px] text-muted-foreground">Journal de preuve (ajout seul) · RGPD art. 7.1</p></div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={purgeOld} disabled={loading} data-testid="consents-purge-old" className="h-10 rounded-xl gap-2 text-rose-600 border-rose-200 hover:bg-rose-50 dark:border-rose-900"><Trash2 className="h-4 w-4" /> Supprimer les anciens</Button>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="consents-refresh" className="h-10 rounded-xl gap-2"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser</Button>
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {FILTERS.map(([k, lbl]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`consents-filter-${k}`}
              className={`px-3.5 h-9 rounded-xl text-sm font-medium transition-colors ${filter === k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>{lbl}</button>
          ))}
        </div>

        {loading ? <p className="text-muted-foreground">Chargement…</p> :
          !data || data.items.length === 0 ? (
            <Card className="rounded-2xl p-10 text-center text-muted-foreground" data-testid="consents-empty">Aucun consentement dans cette catégorie.</Card>
          ) : (
            <div className="space-y-2.5" data-testid="consents-list">
              {data.items.map((c) => (
                <Card key={c.id} data-testid={`consent-${c.id}`} className="rounded-2xl p-4">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{c.email || c.identifiant || "—"}</span>
                        <Badge className={`text-xs border-0 ${STCOL[c.statut] || "bg-muted"}`}>{c.statut}</Badge>
                        <Badge variant="outline" className="text-xs">{c.valeur}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{c.texte_affiche}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">Recueilli le {dtf(c.date_heure)} · IP {c.adresse_ip || "—"} · {c.version_libelle}{c.date_retrait ? ` · Retiré le ${dtf(c.date_retrait)} (${c.moyen_retrait})` : ""}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => exportPdf(c)} data-testid={`consent-pdf-${c.id}`} className="rounded-xl gap-1.5 h-9"><FileDown className="h-3.5 w-3.5" /> Preuve PDF</Button>
                      {c.statut === "actif" && <Button variant="outline" size="sm" onClick={() => withdraw(c.id)} data-testid={`consent-withdraw-${c.id}`} className="rounded-xl gap-1.5 h-9 text-rose-600 border-rose-200 hover:bg-rose-50"><Ban className="h-3.5 w-3.5" /> Retirer</Button>}
                      <Button variant="ghost" size="icon" onClick={() => del(c.id)} data-testid={`consent-delete-${c.id}`} className="rounded-xl h-9 w-9 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
      </main>
    </div>
  );
}
